import { groupByAsMap } from '@aline/common';
import type { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';

interface ObjWithAssignment {
  assignee: UserSubset | null;
  assigneeRoleName: string | null;
}

interface UnusedActionPermission extends Pick<Apollo.WorkflowPermission, 'id' | 'roleName'> {
  account: { id: UUID } | null;
}

export interface UnusedAction extends ObjWithAssignment {
  __typename: Apollo.WorkflowAction['__typename'];
  permission: UnusedActionPermission;
}

/** One line per person or placeholder that would be removed, e.g. "Sam (Buyer) - signatures." */
export function createUnusedActionsCopy(unusedActions: UnusedAction[]): string[] {
  const actionsByHolder = groupByAsMap(
    action => getUnusedActionHolderKey(action.permission),
    unusedActions
  );

  return [...actionsByHolder.values()].map(actions => {
    const [firstAction] = actions;
    const actionTypenames = new Set(actions.map(action => action.__typename));
    const actionsDisplayText = [...actionTypenames]
      .map(typename => getActionDisplayText(typename).toLowerCase())
      .join(', ');

    return `${getAssignmentString(firstAction)} - ${actionsDisplayText}.`;
  });
}

export function getAssignmentString(objWithAssignment: ObjWithAssignment): string {
  const assignmentStringParts: string[] = [];

  if (objWithAssignment.assignee)
    assignmentStringParts.push(objWithAssignment.assignee.displayName);
  if (objWithAssignment.assigneeRoleName)
    assignmentStringParts.push(
      assignmentStringParts.length
        ? `(${objWithAssignment.assigneeRoleName})`
        : objWithAssignment.assigneeRoleName
    );
  if (!assignmentStringParts.length) return 'Unassigned';

  return assignmentStringParts.join(' ');
}

/**
 * Who would be removed. A role placeholder is its own seat even when filled, and two placeholders
 * may share a label, so it goes by its permission. A person with no role can hold more than one
 * role-less permission (one per timeliness), so they go by their account and print once. The
 * prefixes keep the two id kinds from colliding in one map.
 */
function getUnusedActionHolderKey(permission: UnusedActionPermission): string {
  if (permission.roleName || !permission.account) return `permission:${permission.id}`;
  return `account:${permission.account.id}`;
}

function getActionDisplayText(type: Apollo.WorkflowAction['__typename']): string {
  switch (type) {
    case 'WorkflowRecipient':
      return 'Signatures';
    case 'Task':
      return 'Tasks';
  }
}
